import type {
  CustomMetric,
  MetricsConfig,
  LoggerContext,
  Logger,
} from "./types";

export class MetricsCollector {
  private config: MetricsConfig;
  private metrics: CustomMetric[] = [];
  private logger?: Logger;

  constructor(config: MetricsConfig, logger?: Logger) {
    this.config = config;
    this.logger = logger;
  }

  recordMetric(
    name: string,
    value: number,
    unit: CustomMetric["unit"] = "custom",
    tags?: Record<string, string>,
    context?: LoggerContext,
  ): void {
    if (!this.config.enabled) {
      return;
    }

    const metric: CustomMetric = {
      name,
      value,
      unit,
      timestamp: Date.now(),
      tags,
      context,
    };

    // Add to collection
    this.metrics.push(metric);

    // Cleanup old metrics
    this.cleanup();

    // Report metric
    this.config.onMetric?.(metric);

    this.logger?.debug(`Custom metric recorded`, { metric });
  }

  recordTiming(name: string, duration: number, context?: LoggerContext): void {
    this.recordMetric(name, duration, "ms", undefined, context);
  }

  recordCounter(
    name: string,
    increment: number = 1,
    context?: LoggerContext,
  ): void {
    this.recordMetric(name, increment, "count", undefined, context);
  }

  recordGauge(name: string, value: number, context?: LoggerContext): void {
    this.recordMetric(name, value, "custom", undefined, context);
  }

  getMetrics(
    name?: string,
    timeRange?: { start: number; end: number },
  ): CustomMetric[] {
    let filtered = this.metrics;

    if (name) {
      filtered = filtered.filter((m) => m.name === name);
    }

    if (timeRange) {
      filtered = filtered.filter(
        (m) => m.timestamp >= timeRange.start && m.timestamp <= timeRange.end,
      );
    }

    return filtered;
  }

  getAggregatedMetrics(
    name: string,
    aggregation: "avg" | "sum" | "min" | "max" | "count",
    timeRange?: { start: number; end: number },
  ): number | null {
    const metrics = this.getMetrics(name, timeRange);

    if (metrics.length === 0) {
      return null;
    }

    switch (aggregation) {
      case "avg":
        return metrics.reduce((sum, m) => sum + m.value, 0) / metrics.length;
      case "sum":
        return metrics.reduce((sum, m) => sum + m.value, 0);
      case "min":
        return Math.min(...metrics.map((m) => m.value));
      case "max":
        return Math.max(...metrics.map((m) => m.value));
      case "count":
        return metrics.length;
      default:
        return null;
    }
  }

  private cleanup(): void {
    const cutoff = Date.now() - this.config.retentionPeriod;
    this.metrics = this.metrics.filter((m) => m.timestamp > cutoff);

    // Limit total number of metrics
    if (this.metrics.length > this.config.maxMetrics) {
      this.metrics = this.metrics.slice(-this.config.maxMetrics);
    }
  }

  clear(): void {
    this.metrics = [];
  }
}
